import { memo, useId } from 'react';
import type { Side } from '@/engine';

/** suit order of the rows and columns, top to bottom and left to right: ♠ ♥ ♦ ♣ (highest first) */
export const SUITS = ['s', 'h', 'd', 'c'] as const;
export type Suit = (typeof SUITS)[number];
/** outline bodies on a 24-box (lucide shapes); spade and club get a small filled triangle for a stem */
const BODY: Record<Suit, string> = {
  s: 'M5 9c-1.5 1.5-3 3.2-3 5.5A5.5 5.5 0 0 0 7.5 20c1.8 0 3-.5 4.5-2 1.5 1.5 2.7 2 4.5 2a5.5 5.5 0 0 0 5.5-5.5c0-2.3-1.5-4-3-5.5l-7-7-7 7Z',
  h: 'M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5',
  d: 'M2.7 10.3a2.41 2.41 0 0 0 0 3.41l7.59 7.59a2.41 2.41 0 0 0 3.41 0l7.59-7.59a2.41 2.41 0 0 0 0-3.41l-7.59-7.59a2.41 2.41 0 0 0-3.41 0Z',
  c: 'M17.28 9.05a5.5 5.5 0 1 0-10.56 0A5.5 5.5 0 1 0 12 17.66a5.5 5.5 0 1 0 5.28-8.6Z',
};
const STEM: Partial<Record<Suit, string>> = {
  s: 'M12 16.5 10 23h4Z',
  c: 'M12 16 10 23h4Z',
};
/**
 * Outline suit icon; `suit-x` carries the suit for the coloured-suits mode. The stroke is an *inner* stroke (as in
 * Photoshop): it is clipped to the body, so the outer silhouette stays the shape and the line grows inward.
 */
export function SuitIcon({ suit }: { suit: Suit }) {
  const id = useId();
  return (
    <svg className={`suit-${suit}`} viewBox="0 0 24 24" aria-hidden>
      <clipPath id={id}>
        <path d={BODY[suit]} />
      </clipPath>
      <path
        d={BODY[suit]}
        fill="none"
        stroke="currentColor"
        strokeWidth={3.4}
        strokeLinejoin="round"
        clipPath={`url("#${id}")`}
      />
      {STEM[suit] && <path d={STEM[suit]} fill="currentColor" />}
    </svg>
  );
}

interface Props {
  /** what the grid counts (for the accessible label) */
  a: number;
  /** which combos count: both = all, offsuit = off the diagonal, suited = the diagonal */
  side: Side;
  /** lines blocked by cards in our hand (suit indices), shown in the blocker colour */
  blockedRows?: readonly number[] | undefined;
  blockedCols?: readonly number[] | undefined;
}

/**
 * The 16 suit combos of a hand as a 4×4 grid of squares (rows: first card's suit, columns: second's; the
 * diagonal is the suited combos), with the suit glyphs as row and column headers. One colour (--combo, editable
 * in Settings); the combos A does not count are dimmed, and blocked rows and columns are outlined in the blocker
 * colour (--blocker), their headers too. Three <html> attributes style the suits: `data-glow` (Settings) adds the
 * neon glow to squares and icons, `data-suits` (play HUD) shows each square's two suit icons, `data-suit-colors`
 * (play HUD) colours the icons per suit.
 */
export const ComboCards = memo(function ComboCards({ a, side, blockedRows, blockedCols }: Props) {
  return (
    <div className="combos" role="img" aria-label={String(a)}>
      <span className="suit" />
      {SUITS.map((s, c) => (
        <span key={`c${s}`} className={`suit${blockedCols?.includes(c) ? ' blocked' : ''}`}>
          <SuitIcon suit={s} />
        </span>
      ))}
      {SUITS.map((s1, r) => [
        <span key={`r${s1}`} className={`suit${blockedRows?.includes(r) ? ' blocked' : ''}`}>
          <SuitIcon suit={s1} />
        </span>,
        ...SUITS.map((s2, c) => {
          const suited = s1 === s2;
          const on = side === 'both' || (side === 'suited') === suited;
          const blocked = blockedRows?.includes(r) || blockedCols?.includes(c);
          const cls = `combo${on ? '' : ' off'}${blocked ? ' blocked' : ''}`;
          return (
            <span key={s1 + s2} className={cls}>
              <SuitIcon suit={s1} />
              <SuitIcon suit={s2} />
            </span>
          );
        }),
      ])}
    </div>
  );
});
